import { useEffect } from 'react';

interface SchemaOrgProps {
  type: 'Organization' | 'WebSite' | 'WebPage' | 'BreadcrumbList' | 'Article' | 'FAQPage';
  data: Record<string, unknown>;
}

export default function SchemaOrg({ type, data }: SchemaOrgProps) {
  useEffect(() => {
    const scriptId = `schema-${type}`;
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement('script');
    script.id = scriptId;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': type,
      ...data,
    });
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [type, data]);

  return null;
}

// Pre-built schemas
export function makeOrganizationSchema() {
  return {
    type: 'Organization' as const,
    data: {
      name: '韩漫大全',
      url: 'https://www.example.com/',
      description: '免费韩漫大全，漫画网址，韩漫在线阅读，资源导航，便捷访问。韩国漫画资源导航平台。',
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'contact@manhwa123.com',
        contactType: 'customer service',
      },
    },
  };
}

export function makeWebSiteSchema() {
  return {
    type: 'WebSite' as const,
    data: {
      name: '韩漫大全',
      url: 'https://www.example.com/',
      description: '免费韩漫大全，漫画网址，韩漫在线阅读，资源导航。',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://www.example.com/search?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
  };
}

export function makeWebPageSchema(title: string, description: string, url: string) {
  return {
    type: 'WebPage' as const,
    data: {
      name: title,
      description,
      url,
    },
  };
}

export function makeBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    type: 'BreadcrumbList' as const,
    data: {
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.url,
      })),
    },
  };
}

export function makeArticleSchema(
  title: string,
  description: string,
  author: string,
  datePublished: string,
  imageUrl: string,
) {
  return {
    type: 'Article' as const,
    data: {
      headline: title,
      description,
      author: {
        '@type': 'Person',
        name: author,
      },
      datePublished,
      image: imageUrl,
      publisher: {
        '@type': 'Organization',
        name: '韩漫大全',
      },
    },
  };
}

export function makeFAQSchema(
  questions: { question: string; answer: string }[],
) {
  return {
    type: 'FAQPage' as const,
    data: {
      mainEntity: questions.map((q) => ({
        '@type': 'Question',
        name: q.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: q.answer,
        },
      })),
    },
  };
}